import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { ProvidersBunchPayload } from "../../gameCenter/providers/slice";
import sanitizeFileName from "helpers/sanitizeFileName";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface PaymentMethod {
  id?: number;
  title: string;
  code: string;
  description: string;
  image?: string | File;
  isActive: boolean | number;
  isTest?: boolean | number;
  soon?: boolean | number;
  transactionType: "deposit" | "withdrawal";
  providerId: number;
  providerTitle?: string;
  currencyType?: "fiat" | "crypto";
}

/**
 * Main-tab editor state. PaymentMethod keeps its required fields.
 * The editor fills those fields in through partial updates, including a new method
 * that starts empty.
 */
type PaymentMethodDraft = Partial<PaymentMethod>;

function mergePaymentMethodDraft(
  current: PaymentMethodDraft | undefined,
  patch: PaymentMethodDraft,
): PaymentMethodDraft {
  return {
    ...current,
    ...patch,
  };
}
export interface CashierField {
  code: string;
  title: string;
}

export const AUTO_WITHDRAWAL_CONFIG_KEYS = [
  "autoWithdrawalEnabled",
  "autoWithdrawalThreshold",
  "autoWithdrawalThresholdCurrency",
] as const;

export const CLIENT_METHOD_TYPE_CONFIG_KEY = "clientMethodType";
export const CLIENT_METHOD_TYPE_MAX_LENGTH = 255;

export const ALLOW_ZERO_AMOUNT_CONFIG_KEY = "allowZeroAmount";

/**
 * Active and Soon cannot both be on. Turning one on turns the other off.
 * Turning either off leaves the other switch unchanged. Test is not part of this pair.
 */
export function resolvePaymentMethodSwitchState(
  current: { isActive?: boolean | number; soon?: boolean | number },
  field: "isActive" | "soon",
): { isActive: boolean; soon: boolean } {
  const isActive = Boolean(current.isActive);
  const soon = Boolean(current.soon);
  if (field === "isActive") {
    const nextActive = !isActive;
    return { isActive: nextActive, soon: nextActive ? false : soon };
  }
  const nextSoon = !soon;
  return { isActive: nextSoon ? false : isActive, soon: nextSoon };
}

/**
 * Keeps allowZeroAmount only when it is boolean true.
 * Strings such as "true" are removed so they are never saved.
 */
export function applyAllowZeroAmountConfig(
  config: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...config };
  if (next[ALLOW_ZERO_AMOUNT_CONFIG_KEY] === true) {
    next[ALLOW_ZERO_AMOUNT_CONFIG_KEY] = true;
    return next;
  }
  delete next[ALLOW_ZERO_AMOUNT_CONFIG_KEY];
  return next;
}

export const RESERVED_PAYMENT_CONFIG_KEYS = [
  ...AUTO_WITHDRAWAL_CONFIG_KEYS,
  CLIENT_METHOD_TYPE_CONFIG_KEY,
  ALLOW_ZERO_AMOUNT_CONFIG_KEY,
] as const;

export interface PaymentMethodConfigParams {
  [key: string]: unknown;
  autoWithdrawalEnabled?: boolean;
  autoWithdrawalThreshold?: string;
  autoWithdrawalThresholdCurrency?: string | null;
  clientMethodType?: string;
  allowZeroAmount?: boolean;
}

export interface PaymentMethodConfig {
  id: number | string;
  minAmount: string;
  maxAmount: string;
  type: string;
  currency: string;
  country: string;
  isActive: boolean | number;
  config: PaymentMethodConfigParams;
  sdkEnabled?: number | boolean;
  sdkProvider?: string;
  cashierFields?: CashierField[];
  fromCompletedDepositCount?: number;
  toCompletedDepositCount?: number;
  rewardPercent?: number | string | null;
  rewardCalculationLimit?: string | null;
  rewardCalculationLimitTargetCurrency?: string | null;
  isVip?: boolean;
  isTop?: boolean;
  isPopular?: boolean;
  isNew?: boolean;
}

interface PaymentMethodsState {
  list: PaymentMethod[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  selectedItems: number[];
  payload?: PaymentMethodDraft;
  config?: PaymentMethodConfig;
  isItemFetching: boolean;
  processingItemId?: PaymentMethod["id"];
  cashierFields: CashierField[];
  configRequestId?: string;
}
interface GetListPayload {
  limit: number;
  offset: number;
  title: string;
  isActive?: number;
  providerTitle: string;
  transactionType: string;
  order: {
    title?: string;
    isActive?: string;
    id?: string;
    transactionType?: string;
    providerTitle?: string;
  };
}

export const getPaymentMethodsList = createAsyncThunk(
  "payment-methods/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.paymentMethodList}`, {
        ...data,
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const createPaymentMethod = createAsyncThunk<
  number,
  PaymentMethodDraft,
  { rejectValue: ApiErrorPayload }
>(
  "payment-methods/create",
  async (payload: PaymentMethodDraft, { rejectWithValue }) => {
    const data = new FormData();

    if (payload.image && payload.image instanceof File) {
      const safeName = sanitizeFileName(payload.image.name);

      const safeFile = new File([payload.image], safeName, {
        type: payload.image.type,
      });

      data.append("image", safeFile);
    }
    data.append("title", payload?.title ?? "");
    data.append("code", payload?.code ?? "");
    data.append("description", payload.description ?? "");
    data.append("isActive", payload.isActive ? "1" : "0");
    data.append("isTest", payload.isTest ? "1" : "0");
    data.append("soon", payload.soon ? "1" : "0");
    data.append("transactionType", payload.transactionType ?? "");
    data.append("providerId", payload.providerId?.toString() ?? "");
    data.append("currencyType", payload.currencyType ?? "fiat");

    try {
      const result = await instance.post(
        `${apiRoutes.paymentMethodCreate}`,
        data,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return result.data.id;
    } catch (error: any) {
      const apiError = error?.data?.error ?? error?.data;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);
export const updatePaymentMethod = createAsyncThunk<
  boolean,
  PaymentMethodDraft,
  { rejectValue: ApiErrorPayload }
>(
  "payment-methods/update",
  async (payload: PaymentMethodDraft, { rejectWithValue }) => {
    const data = new FormData();
    if (payload.image && payload.image instanceof File) {
      const safeName = sanitizeFileName(payload.image.name);

      const safeFile = new File([payload.image], safeName, {
        type: payload.image.type,
      });

      data.append("image", safeFile);
    }
    data.append("title", (payload?.title as string) ?? "");
    data.append("code", payload?.code as string);
    data.append("description", (payload.description as string) ?? "");
    data.append("isActive", payload.isActive ? "1" : "0");
    data.append("isTest", payload.isTest ? "1" : "0");
    data.append("soon", payload.soon ? "1" : "0");
    data.append("transactionType", payload.transactionType ?? "");
    data.append(
      "providerId",
      payload.providerId != null ? String(payload.providerId) : "",
    );
    data.append("currencyType", payload.currencyType ?? "fiat");

    try {
      await instance.put(
        `${apiRoutes.paymentMethodUpdate}/${payload.id}`,
        data,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return true;
    } catch (error: any) {
      const apiError = error?.data?.error ?? error?.data;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);
const REWARD_CALCULATION_LIMIT_PATTERN = /^\d+(\.\d+)?$/;
const AUTO_WITHDRAWAL_THRESHOLD_PATTERN = /^\d+(\.\d+)?$/;

function resolveRewardCalculationLimit(
  value: PaymentMethodConfig["rewardCalculationLimit"] | number,
): string | null | undefined {
  if (value === undefined || value === null) {
    return null;
  }
  const text = String(value).trim();
  if (text === "") {
    return null;
  }
  if (!REWARD_CALCULATION_LIMIT_PATTERN.test(text)) {
    return undefined;
  }
  if (/^0+(?:\.0+)?$/.test(text)) {
    return "0";
  }
  return text;
}

export const updatePaymentMethodConfig = createAsyncThunk<
  boolean,
  PaymentMethodConfig,
  { rejectValue: ApiErrorPayload }
>(
  "payment-methods/update-config",
  async (data: PaymentMethodConfig, { rejectWithValue }) => {
    const config = { ...(data.config ?? {}) };
    delete config.autoWithdrawalThresholdCurrency;
    const rawClientMethodType = config[CLIENT_METHOD_TYPE_CONFIG_KEY];
    if (typeof rawClientMethodType === "string") {
      const clientMethodType = rawClientMethodType.trim();
      if (clientMethodType === "") {
        delete config[CLIENT_METHOD_TYPE_CONFIG_KEY];
      } else if (clientMethodType.length > CLIENT_METHOD_TYPE_MAX_LENGTH) {
        return rejectWithValue({
          code: "ValidationError",
          fieldErrors: [
            {
              field: `config.${CLIENT_METHOD_TYPE_CONFIG_KEY}`,
              code: "InvalidValue",
            },
          ],
          globalErrors: [],
        });
      } else {
        config[CLIENT_METHOD_TYPE_CONFIG_KEY] = clientMethodType;
      }
    } else if (
      rawClientMethodType != null &&
      rawClientMethodType !== ""
    ) {
      return rejectWithValue({
        code: "ValidationError",
        fieldErrors: [
          {
            field: `config.${CLIENT_METHOD_TYPE_CONFIG_KEY}`,
            code: "InvalidValue",
          },
        ],
        globalErrors: [],
      });
    } else {
      delete config[CLIENT_METHOD_TYPE_CONFIG_KEY];
    }
    const rawAllowZeroAmount = config[ALLOW_ZERO_AMOUNT_CONFIG_KEY];
    if (
      rawAllowZeroAmount !== undefined &&
      rawAllowZeroAmount !== true &&
      rawAllowZeroAmount !== false &&
      rawAllowZeroAmount !== null &&
      rawAllowZeroAmount !== ""
    ) {
      return rejectWithValue({
        code: "ValidationError",
        fieldErrors: [
          {
            field: `config.${ALLOW_ZERO_AMOUNT_CONFIG_KEY}`,
            code: "InvalidValue",
          },
        ],
        globalErrors: [],
      });
    }
    const normalizedAllowZeroAmount = applyAllowZeroAmountConfig(config);
    delete config[ALLOW_ZERO_AMOUNT_CONFIG_KEY];
    if (normalizedAllowZeroAmount[ALLOW_ZERO_AMOUNT_CONFIG_KEY] === true) {
      config[ALLOW_ZERO_AMOUNT_CONFIG_KEY] = true;
    }
    const autoWithdrawalEnabled = config.autoWithdrawalEnabled === true;
    const autoWithdrawalThreshold =
      config.autoWithdrawalThreshold == null
        ? ""
        : String(config.autoWithdrawalThreshold).trim();
    if (
      (autoWithdrawalEnabled && autoWithdrawalThreshold === "") ||
      (autoWithdrawalThreshold !== "" &&
        !AUTO_WITHDRAWAL_THRESHOLD_PATTERN.test(autoWithdrawalThreshold))
    ) {
      return rejectWithValue({
        code: "ValidationError",
        fieldErrors: [
          {
            field: "config.autoWithdrawalThreshold",
            code: "InvalidValue",
          },
        ],
        globalErrors: [],
      });
    }
    if (autoWithdrawalThreshold === "") {
      delete config.autoWithdrawalThreshold;
    } else {
      config.autoWithdrawalThreshold = autoWithdrawalThreshold;
    }
    if ("autoWithdrawalEnabled" in config) {
      config.autoWithdrawalEnabled = autoWithdrawalEnabled;
    }

    const rewardPercent = Number(data.rewardPercent ?? 0);
    if (!Number.isInteger(rewardPercent) || rewardPercent < 0) {
      return rejectWithValue({
        code: "ValidationError",
        fieldErrors: [{ field: "rewardPercent", code: "InvalidValue" }],
        globalErrors: [],
      });
    }
    const rewardCalculationLimit = resolveRewardCalculationLimit(
      data.rewardCalculationLimit,
    );
    if (rewardCalculationLimit === undefined) {
      return rejectWithValue({
        code: "ValidationError",
        fieldErrors: [{ field: "rewardCalculationLimit", code: "InvalidValue" }],
        globalErrors: [],
      });
    }

    try {
      await instance.put(`${apiRoutes.paymentMethodSetConfig}/${data.id}`, {
        ...data,
        config,
        isActive: data.isActive ? 1 : 0,
        rewardPercent,
        rewardCalculationLimit,
        rewardCalculationLimitTargetCurrency: data.rewardCalculationLimitTargetCurrency
          ? String(data.rewardCalculationLimitTargetCurrency).trim().toUpperCase()
          : null,
        isVip: data.isVip ? 1 : 0,
        isTop: data.isTop ? 1 : 0,
        isPopular: data.isPopular ? 1 : 0,
        isNew: data.isNew ? 1 : 0,
      });
      return true;
    } catch (error: any) {
      const apiError = error?.data?.error ?? error?.data;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);
export const getPaymentMethodConfig = createAsyncThunk<
  any,
  { id: string; country: string; currency: string },
  { rejectValue: ApiErrorPayload }
>(
  "payment-methods/get-config",
  async (
    data: { id: string; country: string; currency: string },
    { rejectWithValue },
  ) => {
    try {
      const result = await instance.get(
        `${apiRoutes.paymentMethodGetConfig}/${data.id}/${data.country}/${data.currency}`,
      );
      return result.data;
    } catch (error: any) {
      const apiError = error?.data?.error ?? error?.data;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

export const getPaymentMethodCashierFields = createAsyncThunk<
  any,
  void,
  { rejectValue: ApiErrorPayload }
>("payment-methods/cashier-fields", async (_, { rejectWithValue }) => {
  try {
    const result = await instance.get(apiRoutes.paymentMethodCashierFields);

    return result.data;
  } catch (error: any) {
    const apiError = error?.data?.error ?? error?.data;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const getPaymentMethod = createAsyncThunk(
  "payment-methods/read",
  async (id: string | number) => {
    try {
      const result = await instance.get(`${apiRoutes.paymentMethodItem}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const deletePaymentMethod = createAsyncThunk(
  "payment-methods/delete",
  async (id: string | number) => {
    try {
      await instance.delete(`${apiRoutes.paymentMethodDelete}/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const updatePaymentMethodBunch = createAsyncThunk(
  "payment-methods/update-bunch",
  async (data: ProvidersBunchPayload) => {
    try {
      await instance.put(`${apiRoutes.paymentMethodUpdateBunch}`, {
        ...data,
        isActive: data.isActive,
      });
      return true;
    } catch (error) {
      return false;
    }
  },
);

const initialState: PaymentMethodsState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  isItemFetching: false,
  cashierFields: [],
};

export const paymentMethodsSlice = createSlice({
  name: "payment-methods",
  initialState,
  reducers: {
    handleUpdateFields: (
      state: PaymentMethodsState,
      action: PayloadAction<PaymentMethodDraft>,
    ) => {
      state.payload = mergePaymentMethodDraft(state.payload, action.payload);
    },
    handleUpdateFieldsConfig: (
      state: PaymentMethodsState,
      action: PayloadAction<Partial<PaymentMethodConfig>>,
    ) => {
      state.config = {
        ...state.config,
        ...action.payload,
      } as PaymentMethodConfig;
    },
    handleSelectPaymentMethodConfigScope: (
      state: PaymentMethodsState,
      action: PayloadAction<{ country: string; currency: string }>,
    ) => {
      if (
        state.config?.country === action.payload.country &&
        state.config?.currency === action.payload.currency
      ) {
        return;
      }
      state.config = {
        country: action.payload.country,
        currency: action.payload.currency,
        config: {},
      } as PaymentMethodConfig;
      state.configRequestId = undefined;
    },
    handleClearFields: (state: PaymentMethodsState) => {
      state.payload = undefined;
      state.config = undefined;
      state.configRequestId = undefined;
      state.isItemFetched = false;
    },
    handlePaginate: (
      state: PaymentMethodsState,
      action: PayloadAction<number>,
    ) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleSelectItem: (
      state: PaymentMethodsState,
      action: PayloadAction<number>,
    ) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: PaymentMethodsState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id ?? 0);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPaymentMethodsList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(createPaymentMethod.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createPaymentMethod.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });
    builder.addCase(createPaymentMethod.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updatePaymentMethod.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updatePaymentMethod.fulfilled, (state, action) => {
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, {
              ...action.meta.arg,
              image:
                action.meta.arg.image instanceof File
                  ? item.image
                  : action.meta.arg.image,
            })
          : item,
      );
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updatePaymentMethod.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(getPaymentMethod.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.payload = action.payload
        ? {
            ...action.payload,
            isTest:
              action.payload.isTest === true ||
              action.payload.isTest === 1 ||
              action.payload.isTest === "1",
            soon:
              action.payload.soon === true ||
              action.payload.soon === 1 ||
              action.payload.soon === "1",
          }
        : undefined;
    });
    builder.addCase(updatePaymentMethodConfig.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = Number(action.meta.arg.id);
    });
    builder.addCase(updatePaymentMethodConfig.fulfilled, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updatePaymentMethodConfig.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(deletePaymentMethod.pending, (state, action) => {
      state.processingItemId = +action.meta.arg || undefined;
    });
    builder.addCase(deletePaymentMethod.fulfilled, (state) => {
      state.isListFetched = false;
      state.processingItemId = undefined;
    });
    builder.addCase(deletePaymentMethod.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(updatePaymentMethodBunch.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
    builder.addCase(getPaymentMethodConfig.fulfilled, (state, action) => {
      if (
        state.configRequestId !== action.meta.requestId ||
        (state.config != null &&
          (state.config.country !== action.meta.arg.country ||
            state.config.currency !== action.meta.arg.currency))
      ) {
        return;
      }
      const loadedLimit = resolveRewardCalculationLimit(
        action.payload?.rewardCalculationLimit,
      );
      const loadedConfig = { ...(action.payload?.config ?? {}) };
      loadedConfig.autoWithdrawalEnabled =
        loadedConfig.autoWithdrawalEnabled === true;
      if (loadedConfig[ALLOW_ZERO_AMOUNT_CONFIG_KEY] === true) {
        loadedConfig[ALLOW_ZERO_AMOUNT_CONFIG_KEY] = true;
      } else {
        delete loadedConfig[ALLOW_ZERO_AMOUNT_CONFIG_KEY];
      }
      state.config = {
        ...action.payload,
        config: loadedConfig,
        rewardPercent: action.payload?.rewardPercent || 0,
        rewardCalculationLimit: loadedLimit === undefined ? null : loadedLimit,
        rewardCalculationLimitTargetCurrency:
          action.payload?.rewardCalculationLimitTargetCurrency || null,
        isVip: action.payload?.isVip === true,
        isTop: action.payload?.isTop === true,
        isPopular: action.payload?.isPopular === true,
        isNew: action.payload?.isNew === true,
      };
      state.configRequestId = undefined;
    });
    builder.addCase(getPaymentMethodConfig.pending, (state, action) => {
      state.configRequestId = action.meta.requestId;
    });
    builder.addCase(getPaymentMethodConfig.rejected, (state, action) => {
      if (state.configRequestId === action.meta.requestId) {
        state.configRequestId = undefined;
      }
    });
    builder.addCase(
      getPaymentMethodCashierFields.fulfilled,
      (state, action) => {
        state.cashierFields = action.payload?.items ?? [];
      },
    );
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleUpdateFields,
  handleClearFields,
  handleUpdateFieldsConfig,
  handleSelectPaymentMethodConfigScope,
} = paymentMethodsSlice.actions;
